/**
 * logger.js - 简单的日志记录工具
 * 用于跟踪API调用和应用程序错误
 */

// 日志级别
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// 当前日志级别
let currentLogLevel = LOG_LEVELS.INFO;

// 储存的日志
const logHistory = [];
const MAX_LOG_ENTRIES = 100;

/**
 * 设置日志级别
 * @param {number} level - 日志级别
 */
function setLogLevel(level) {
  if (level >= LOG_LEVELS.DEBUG && level <= LOG_LEVELS.ERROR) {
    currentLogLevel = level;
  }
}

/**
 * 内部日志记录函数
 * @param {string} level - 日志级别标签
 * @param {string} message - 日志消息
 * @param {any} data - 附加数据
 * @param {string} color - 控制台颜色
 */
function logMessage(level, message, data = null, color) {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    level,
    message,
    data: data ? (typeof data === 'object' ? JSON.stringify(data) : data) : null
  };
  
  // 添加到历史记录
  logHistory.unshift(logEntry);
  if (logHistory.length > MAX_LOG_ENTRIES) {
    logHistory.pop();
  }
  
  // 控制台输出
  const consoleMessage = `[${timestamp}] [${level}] ${message}`;
  if (data) {
    console[level.toLowerCase()](
      `%c${consoleMessage}`, 
      `color: ${color}; font-weight: bold`,
      data
    );
  } else {
    console[level.toLowerCase()](
      `%c${consoleMessage}`, 
      `color: ${color}; font-weight: bold`
    );
  }
  
  // 如果是错误，可以考虑发送到服务器或存储到localStorage
  if (level === 'ERROR') {
    try {
      const existingLogs = JSON.parse(localStorage.getItem('hammerath_error_logs') || '[]');
      existingLogs.unshift(logEntry);
      if (existingLogs.length > 50) existingLogs.pop();
      localStorage.setItem('hammerath_error_logs', JSON.stringify(existingLogs));
    } catch (e) {
      // 如果localStorage不可用，静默失败
    }
  }
}

/**
 * 调试日志
 * @param {string} message - 日志消息
 * @param {any} data - 附加数据
 */
function debug(message, data = null) {
  if (currentLogLevel <= LOG_LEVELS.DEBUG) {
    logMessage('DEBUG', message, data, '#6c757d');
  }
}

/**
 * 信息日志
 * @param {string} message - 日志消息
 * @param {any} data - 附加数据
 */
function info(message, data = null) {
  if (currentLogLevel <= LOG_LEVELS.INFO) {
    logMessage('INFO', message, data, '#17a2b8');
  }
}

/**
 * 警告日志
 * @param {string} message - 日志消息
 * @param {any} data - 附加数据
 */
function warn(message, data = null) {
  if (currentLogLevel <= LOG_LEVELS.WARN) {
    logMessage('WARN', message, data, '#ffc107');
  }
}

/**
 * 错误日志
 * @param {string} message - 日志消息
 * @param {any} data - 附加数据
 */
function error(message, data = null) {
  if (currentLogLevel <= LOG_LEVELS.ERROR) {
    logMessage('ERROR', message, data, '#dc3545');
  }
}

/**
 * 获取日志历史
 * @returns {Array} 日志历史
 */
function getHistory() {
  return [...logHistory];
}

/**
 * 清除日志历史
 */
function clearHistory() {
  logHistory.length = 0;
}

/**
 * 对API调用进行日志记录
 * @param {string} endpoint - API端点
 * @param {object} requestData - 请求数据
 * @param {string} status - 请求状态
 * @param {any} responseData - 响应数据
 */
function logApiCall(endpoint, requestData, status, responseData = null) {
  const level = status === 'success' ? 'INFO' : 'ERROR';
  const color = status === 'success' ? '#28a745' : '#dc3545';
  
  logMessage(
    level, 
    `API调用 ${endpoint} - ${status}`, 
    {
      request: requestData,
      response: responseData,
      status
    },
    color
  );
}

export default {
  LOG_LEVELS,
  setLogLevel,
  debug,
  info,
  warn,
  error,
  getHistory,
  clearHistory,
  logApiCall
}; 